/**
 * Unified utility for fetching and transforming protocol metadata from BD SDK
 * This eliminates duplication across multiple files that fetch the same data
 */

import { normalizeChainIdString } from "@blockdaemon/bd-sdk/src/main/shared/caip2";
import { blockdaemonSDK } from "../../lib/sdk";

type ResponseDataWithArrays = {
  protocols?: unknown[];
  items?: unknown[];
  data?: unknown[];
};

type ProtocolItem = {
  protocolName?: string;
  protocolLogo?: string;
  supportedChains?: Array<{
    chainId?: string;
    chainName?: string;
    protocolId?: string;
    chainLogo?: string;
  }>;
};

/**
 * Protocol metadata item from BD SDK response
 */
interface ProtocolMetadataItem {
  protocolName: string;
  protocolLogo: string;
  supportedChains: Array<{
    chainId: string;
    chainName: string;
    protocolId: string;
    chainLogo?: string;
  }>;
}

/**
 * Fetch protocol metadata from BD SDK for lend/borrow protocols
 * This is the single source of truth for protocol metadata fetching
 *
 * @returns Array of protocol metadata items or empty array on error
 */
export async function fetchProtocolMetadata(): Promise<ProtocolMetadataItem[]> {
  try {
    const response = await blockdaemonSDK.defi.getApiMetadataV2({
      category: "lendborrow",
      endpoint: "getpool",
    });

    // Check if response is successful and has data
    if (!response || !("data" in response) || !response.data) {
      return [];
    }

    // Handle different response structures - data might be an object with a nested array
    let dataArray: unknown[] = [];
    const responseData = response.data;

    if (Array.isArray(responseData)) {
      dataArray = responseData;
    } else if (responseData && typeof responseData === "object") {
      // Check common property names for the actual data array
      const dataWithArrays = responseData as ResponseDataWithArrays;
      if ("protocols" in dataWithArrays && Array.isArray(dataWithArrays.protocols)) {
        dataArray = dataWithArrays.protocols;
      } else if ("items" in dataWithArrays && Array.isArray(dataWithArrays.items)) {
        dataArray = dataWithArrays.items;
      } else if ("data" in dataWithArrays && Array.isArray(dataWithArrays.data)) {
        dataArray = dataWithArrays.data;
      } else {
        // Check if the object is empty before logging a warning
        if (Object.keys(responseData).length === 0) {
          // Empty object - silently return empty array (common in test environments)
          return [];
        }
        // Object has keys but no recognized array properties
        console.warn(
          "BD SDK getApiMetadataV2 response.data is an object but no array found within:",
          Object.keys(responseData),
        );
        return [];
      }
    } else {
      console.warn(
        "BD SDK getApiMetadataV2 response.data is not an array or object:",
        typeof responseData,
      );
      return [];
    }

    // Transform BD SDK response to our standardized format
    return dataArray.map((item) => {
      const protocolItem = item as ProtocolItem;
      return {
        protocolName: protocolItem.protocolName || "",
        protocolLogo: protocolItem.protocolLogo || "",
        supportedChains: (protocolItem.supportedChains || []).map((chain) => {
          return {
            // TODO: Remove normalizeChainIdString() once chainId response is migrated to CAIP-2 format.
            chainId: chain?.chainId ? normalizeChainIdString(chain.chainId) : "",
            chainName: chain.chainName || "",
            protocolId: chain.protocolId || "",
            // Use chainLogo field from BD SDK
            chainLogo: chain.chainLogo || "",
          };
        }),
      };
    });
  } catch (error) {
    console.error("Error fetching protocol metadata from BD SDK:", error);
    return [];
  }
}
